import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { CreditCard, Trash2, Check, AlertTriangle, ExternalLink } from "lucide-react";

export default function StripeSettings({ businessId }) {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState({ api_key: "", enabled: true });

  async function load() {
    setLoading(true);
    const { data } = await api.get(`/businesses/${businessId}/stripe`);
    setS(data);
    setForm({ api_key: "", enabled: data.enabled !== false });
    setLoading(false);
  }
  useEffect(() => { load(); }, [businessId]);

  async function save(e) {
    e.preventDefault();
    setSaving(true); setMsg(null);
    try {
      const payload = { enabled: form.enabled };
      if (form.api_key) payload.api_key = form.api_key;
      await api.put(`/businesses/${businessId}/stripe`, payload);
      setMsg({ type: "ok", text: "Impostazioni salvate." });
      await load();
    } catch (ex) {
      setMsg({ type: "err", text: ex.response?.data?.detail || "Errore nel salvataggio." });
    } finally {
      setSaving(false);
    }
  }

  async function doTest() {
    setTesting(true); setMsg(null);
    try {
      await api.post(`/businesses/${businessId}/stripe/test`);
      setMsg({ type: "ok", text: "Connessione Stripe OK. Le caparre funzioneranno." });
      await load();
    } catch (ex) {
      setMsg({ type: "err", text: ex.response?.data?.detail || "Test fallito." });
    } finally {
      setTesting(false);
    }
  }

  async function removeConfig() {
    if (!window.confirm("Rimuovere le credenziali Stripe? Le caparre online verranno disabilitate.")) return;
    await api.delete(`/businesses/${businessId}/stripe`);
    await load();
    setMsg({ type: "ok", text: "Credenziali rimosse." });
  }

  if (loading) return <div className="p-6 text-sm text-zinc-500 dark:text-zinc-400">Caricamento…</div>;

  return (
    <div className="max-w-3xl space-y-6" data-testid="stripe-settings">
      <div>
        <h3 className="font-display text-xl font-medium flex items-center gap-2"><CreditCard className="h-5 w-5" /> Pagamenti online</h3>
        <p className="text-sm text-zinc-500 mt-1 dark:text-zinc-400">
          Configura il tuo account Stripe per incassare caparre direttamente sul tuo conto. Prenotoo non trattiene nulla, il denaro va sul tuo Stripe.
        </p>
      </div>

      <div className="rounded-3xl border border-zinc-200 bg-zinc-50 p-5 text-sm space-y-2 dark:border-zinc-700 dark:bg-zinc-800">
        <div className="font-semibold text-zinc-800 dark:text-zinc-200">Come iniziare</div>
        <ol className="list-decimal list-inside space-y-1.5 text-zinc-700 text-[13px] leading-relaxed dark:text-zinc-300">
          <li>Crea un account su <a href="https://dashboard.stripe.com/register" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">dashboard.stripe.com <ExternalLink className="h-3 w-3" /></a> (gratis, no canone, commissione ~1.4% + €0.25 per pagamento europeo).</li>
          <li>Completa i dati attività e conto bancario (per ricevere i bonifici automatici).</li>
          <li>Vai su <em>Sviluppatori → Chiavi API</em>, copia la <strong>Secret key</strong> (inizia con <code className="bg-zinc-200 px-1.5 py-0.5 rounded text-[11px] dark:bg-zinc-900/40">sk_live_</code> in produzione o <code className="bg-zinc-200 px-1.5 py-0.5 rounded text-[11px] dark:bg-zinc-900/40">sk_test_</code> per test).</li>
          <li>Incollala qui sotto e premi <strong>Verifica connessione</strong>. Dopo il test, i servizi con caparra attiva useranno il tuo account.</li>
        </ol>
      </div>

      {msg && (
        <div className={`rounded-2xl px-4 py-3 text-sm flex items-start gap-2 ${msg.type === "ok" ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300" : "bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-300"}`} data-testid="stripe-msg">
          {msg.type === "ok" ? <Check className="h-4 w-4 mt-0.5 shrink-0" /> : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />}
          <span>{msg.text}</span>
        </div>
      )}

      <form onSubmit={save} className="card-soft p-5 space-y-5">
        <div>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">Stripe Secret Key *</span>
            <input
              type="password"
              placeholder={s?.api_key_masked ? `Attuale: ${s.api_key_masked} (lascia vuoto per mantenerla)` : "sk_live_xxxxxxxxxxxxxxxxxxxx oppure sk_test_..."}
              value={form.api_key}
              onChange={(e) => setForm({ ...form, api_key: e.target.value })}
              className="input-soft mt-2"
              data-testid="stripe-api-key"
              autoComplete="new-password"
            />
          </label>
          <p className="text-[11px] text-zinc-500 mt-1 dark:text-zinc-400">Memorizzata cifrata (Fernet AES). Non visibile nel codice, mai mostrata in chiaro.</p>
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="h-4 w-4 accent-blue-600" data-testid="stripe-enabled-toggle" />
          <span>Caparre online attive</span>
        </label>

        <div className="flex flex-wrap gap-2 justify-end border-t border-zinc-100 pt-4 dark:border-zinc-800">
          {s?.configured && (
            <button type="button" onClick={removeConfig} className="h-10 px-4 rounded-full text-xs border border-red-300 text-red-700 hover:bg-red-50 inline-flex items-center dark:text-red-300 dark:bg-red-950/40" data-testid="stripe-remove">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Rimuovi
            </button>
          )}
          {s?.configured && (
            <button type="button" onClick={doTest} disabled={testing} className="btn-secondary h-10 px-4 text-xs" data-testid="stripe-test">
              {testing ? "Verifica…" : "Verifica connessione"}
            </button>
          )}
          <button type="submit" disabled={saving} className="btn-accent h-10 px-5 text-xs" data-testid="stripe-save">
            {saving ? "Salvataggio…" : (s?.configured ? "Aggiorna" : "Salva e attiva")}
          </button>
        </div>
      </form>

      {s?.configured && (
        <div className="rounded-2xl bg-zinc-50 border border-zinc-200 p-4 text-[13px] dark:bg-zinc-800 dark:border-zinc-700" data-testid="stripe-status">
          <div className="font-semibold mb-1">Stato integrazione</div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-zinc-700 dark:text-zinc-300">
            <span className="text-zinc-500 dark:text-zinc-400">Secret key</span><span className="text-right font-mono text-xs">{s.api_key_masked || "—"}</span>
            <span className="text-zinc-500 dark:text-zinc-400">Modalità</span><span className="text-right"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${s.mode === "live" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" : "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"}`}>{s.mode === "live" ? "LIVE" : "TEST"}</span></span>
            <span className="text-zinc-500 dark:text-zinc-400">Caparre</span><span className="text-right">{s.enabled ? <span className="text-emerald-700 dark:text-emerald-300">Attive</span> : <span className="text-zinc-500 dark:text-zinc-400">Disattivate</span>}</span>
            <span className="text-zinc-500 dark:text-zinc-400">Ultimo test</span><span className="text-right">{s.last_test_at ? (s.last_test_ok ? <span className="text-emerald-700 dark:text-emerald-300">OK · {new Date(s.last_test_at).toLocaleString("it-IT")}</span> : <span className="text-red-700 dark:text-red-300">Fallito</span>) : "—"}</span>
          </div>
          <div className="text-[11px] text-zinc-500 mt-3 dark:text-zinc-400">
            Prenotoo non tocca i tuoi fondi: le caparre pagate dai clienti vengono accreditate direttamente sul tuo conto Stripe. Prenotoo è solo sub-responsabile del trattamento dei dati di prenotazione ai sensi del GDPR.
          </div>
        </div>
      )}
    </div>
  );
}
